// src/pages/Sanitary/SanitaryRulesPage.jsx
// =============================================================================
// Professional overview of sanitary consumption rules.
// =============================================================================
// The overview intentionally contains no inline create/edit form. Maintenance
// happens in SanitaryRuleEditorPage so one row has one clear Edit action.

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import PaginationFooter from "../../components/common/PaginationFooter";
import { fetchStockLocations } from "../../services/stockService";
import {
  SANITARY_QUANTITY_BASES,
  SANITARY_RULE_STATUSES,
  SANITARY_RULE_TYPES,
  deleteSanitaryRule,
  listSanitaryRules,
} from "../../services/sanitaryRuleService";

import "../Stock/StockMasterDataPage.css";
import "./SanitaryProductsPage.css";
import "./SanitaryRulesPage.css";

const RULE_TYPES = Object.values(SANITARY_RULE_TYPES);
const STATUSES = Object.values(SANITARY_RULE_STATUSES);

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatQty(value) {
  if (value === undefined || value === null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, { maximumFractionDigits: 6 })
    : String(value);
}

function productLabel(rule, t) {
  const v = rule?.latestVersion || {};
  if (v.quantity_basis === SANITARY_QUANTITY_BASES.WORKING_SOLUTION) {
    return v.solutionDefinition?.name || t("sanitary:rules.quantityBases.working_solution");
  }
  const product = rule?.product || {};
  return [product.name_public, product.sku ? `SKU ${product.sku}` : null]
    .filter(Boolean)
    .join(" · ") || "—";
}

function locationLabel(location) {
  if (!location) return "—";
  return [location.code, location.name].filter(Boolean).join(" · ") || "—";
}

function quantityLabel(rule, t) {
  const v = rule?.latestVersion;
  if (!v) return "—";
  if (v.quantity_basis === SANITARY_QUANTITY_BASES.WORKING_SOLUTION) {
    return `${formatQty(v.solution_execution_count || 1)} × ${v.solutionDefinition?.name || t("sanitary:rules.quantityBases.working_solution")}`;
  }
  return `${formatQty(v.quantity)} ${v.unit_symbol || v.unit_code || ""}`.trim();
}

function sourceLabel(rule, t) {
  const v = rule?.latestVersion || {};
  if (rule?.rule_type === SANITARY_RULE_TYPES.PER_SERVICE) {
    return v.service
      ? [v.service.name_public, v.service.service_code ? `(${v.service.service_code})` : null].filter(Boolean).join(" ")
      : t("sanitary:rules.sources.noService");
  }
  if (rule?.rule_type === SANITARY_RULE_TYPES.PER_STATION) {
    return v.station_label || v.station_code || t("sanitary:rules.sources.noStation");
  }
  return t(`sanitary:rules.types.${rule?.rule_type}`, { defaultValue: rule?.rule_type || "—" });
}

function statusClass(status) {
  if (status === SANITARY_RULE_STATUSES.ACTIVE) return "sanitary-rule-status sanitary-rule-status--active";
  if (status === SANITARY_RULE_STATUSES.DRAFT) return "sanitary-rule-status sanitary-rule-status--draft";
  if (status === SANITARY_RULE_STATUSES.ARCHIVED) return "sanitary-rule-status sanitary-rule-status--archived";
  return "sanitary-rule-status sanitary-rule-status--inactive";
}

export default function SanitaryRulesPage() {
  const { t } = useTranslation(["sanitary", "common"]);
  const navigate = useNavigate();
  const { authState } = useContext(AuthContext);
  const role = normalizeRole(authState?.user?.role);
  const canEdit = role === "superuser" || role === "reception";

  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [ruleType, setRuleType] = useState("");
  const [status, setStatus] = useState("");
  const [locationId, setLocationId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pageInfo, setPageInfo] = useState({ page: 1, page_size: 25, total: 0, page_count: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshSeq, setRefreshSeq] = useState(0);
  const [deletingRuleId, setDeletingRuleId] = useState(null);

  useEffect(() => {
    let active = true;
    fetchStockLocations({ active: true })
      .then((data) => { if (active) setLocations(asArray(data)); })
      .catch(() => { if (active) setLocations([]); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    listSanitaryRules({
      q: search,
      rule_type: ruleType,
      status,
      location_id: locationId,
      page,
      page_size: pageSize,
    })
      .then((data) => {
        if (!active) return;
        setRows(asArray(data?.items));
        setPageInfo(data?.pagination || { page: 1, page_size: pageSize, total: 0, page_count: 1 });
        const resolvedPage = Number(data?.pagination?.page || page);
        if (resolvedPage !== page) setPage(resolvedPage);
      })
      .catch((err) => {
        if (!active) return;
        setRows([]);
        setError(err?.message || t("sanitary:rules.messages.loadFailed"));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [search, ruleType, status, locationId, page, pageSize, refreshSeq, t]);

  const activeFilters = useMemo(
    () => [search, ruleType, status, locationId].filter(Boolean).length,
    [search, ruleType, status, locationId]
  );

  function applySearch(event) {
    event?.preventDefault?.();
    setPage(1);
    setSearch(searchDraft.trim());
  }

  function clearFilters() {
    setSearchDraft("");
    setSearch("");
    setRuleType("");
    setStatus("");
    setLocationId("");
    setPage(1);
  }

  async function handleDeleteRule(rule) {
    if (!canEdit || !rule?.usage?.can_delete || deletingRuleId) return;
    const confirmed = window.confirm(t("sanitary:rules.messages.deleteConfirm", { name: rule.name }));
    if (!confirmed) return;
    setDeletingRuleId(rule.id);
    setError(null);
    try {
      await deleteSanitaryRule(rule.id);
      setRows((current) => current.filter((item) => String(item.id) !== String(rule.id)));
      setRefreshSeq((value) => value + 1);
    } catch (err) {
      setError(err?.message || t("sanitary:rules.messages.deleteFailed"));
    } finally {
      setDeletingRuleId(null);
    }
  }

  return (
    <main className="stock-md-page sanitary-rules-overview">
      <header className="stock-md-header sanitary-rules-overview__header">
        <div>
          <p className="stock-md-eyebrow">{t("sanitary:rules.eyebrow")}</p>
          <h1 className="stock-md-title">{t("sanitary:rules.title")}</h1>
          <p className="stock-md-subtitle">{t("sanitary:rules.overview.subtitle")}</p>
        </div>
        <div className="sanitary-rules-overview__actions">
          {canEdit ? (
            <button
              type="button"
              className="stock-md-button stock-md-button--primary"
              onClick={() => navigate("/sanitary/rules/new")}
            >
              + {t("sanitary:rules.overview.create")}
            </button>
          ) : null}
          <button
            type="button"
            className="stock-md-button"
            onClick={() => setRefreshSeq((value) => value + 1)}
            disabled={loading}
          >
            {t("common:actions.refresh", { defaultValue: "Odśwież" })}
          </button>
        </div>
      </header>

      {!canEdit ? <div className="stock-md-info-box">{t("sanitary:rules.messages.readOnly")}</div> : null}
      {error ? <div className="stock-md-error">{error}</div> : null}

      <section className="stock-md-card sanitary-rules-overview__filters">
        <form className="sanitary-rules-filter-grid" onSubmit={applySearch}>
          <label className="stock-md-field sanitary-rules-filter-grid__search">
            <span>{t("sanitary:rules.filters.search")}</span>
            <input
              type="search"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder={t("sanitary:rules.filters.searchPlaceholder")}
            />
          </label>
          <label className="stock-md-field">
            <span>{t("sanitary:rules.filters.type")}</span>
            <select value={ruleType} onChange={(event) => { setRuleType(event.target.value); setPage(1); }}>
              <option value="">{t("sanitary:rules.filters.allTypes")}</option>
              {RULE_TYPES.map((value) => (
                <option key={value} value={value}>{t(`sanitary:rules.types.${value}`, { defaultValue: value })}</option>
              ))}
            </select>
          </label>
          <label className="stock-md-field">
            <span>{t("sanitary:rules.filters.status")}</span>
            <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
              <option value="">{t("sanitary:rules.filters.allStatuses")}</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>{t(`sanitary:rules.statuses.${value}`, { defaultValue: value })}</option>
              ))}
            </select>
          </label>
          <label className="stock-md-field">
            <span>{t("sanitary:rules.filters.location")}</span>
            <select value={locationId} onChange={(event) => { setLocationId(event.target.value); setPage(1); }}>
              <option value="">{t("sanitary:common.all")}</option>
              {locations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
            </select>
          </label>
          <div className="sanitary-rules-filter-grid__actions">
            <button type="submit" className="stock-md-button">{t("common:actions.search", { defaultValue: "Szukaj" })}</button>
            {activeFilters ? (
              <button type="button" className="stock-md-button stock-md-button--secondary" onClick={clearFilters}>
                {t("sanitary:rules.overview.clearFilters")}
              </button>
            ) : null}
          </div>
        </form>
      </section>

      <section className="stock-md-card sanitary-rules-overview__browser">
        <div className="sanitary-rules-card-heading">
          <div>
            <h2>{t("sanitary:rules.overview.listTitle")}</h2>
            <p>{t("sanitary:rules.overview.listSubtitle")}</p>
          </div>
          <span className="sanitary-rules-count">{t("sanitary:rules.overview.total", { count: Number(pageInfo.total || 0) })}</span>
        </div>

        <div className="stock-md-table-wrap">
          <table className="stock-md-table stock-md-table--compact sanitary-rules-table">
            <thead>
              <tr>
                <th>{t("sanitary:rules.table.rule")}</th>
                <th>{t("sanitary:rules.table.type")}</th>
                <th>{t("sanitary:rules.overview.columns.consumes")}</th>
                <th>{t("sanitary:rules.table.quantity")}</th>
                <th>{t("sanitary:rules.table.validity")}</th>
                <th>{t("sanitary:rules.table.location")}</th>
                <th>{t("sanitary:rules.table.source")}</th>
                <th>{t("sanitary:rules.table.status")}</th>
                <th>{t("sanitary:rules.overview.columns.usage")}</th>
                <th>{t("sanitary:rules.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" className="stock-md-empty">{t("sanitary:rules.messages.loading")}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="10" className="stock-md-empty">{t("sanitary:rules.overview.emptyFiltered")}</td></tr>
              ) : rows.map((rule) => {
                const version = rule.latestVersion || {};
                const usage = rule.usage || version.usage || {};
                const usageCount = Number(usage.consumption_entries || 0) + Number(usage.hygiene_events || 0);
                return (
                  <tr key={rule.id}>
                    <td>
                      <strong>{rule.name}</strong>
                      <small>{rule.code || `#${rule.id}`}</small>
                    </td>
                    <td>{t(`sanitary:rules.types.${rule.rule_type}`, { defaultValue: rule.rule_type })}</td>
                    <td>{productLabel(rule, t)}</td>
                    <td>{quantityLabel(rule, t)}</td>
                    <td>
                      <strong>{version.valid_from || "—"}</strong>
                      <small>{version.valid_to ? `→ ${version.valid_to}` : t("sanitary:rules.overview.openEnded")}</small>
                    </td>
                    <td>{locationLabel(version.location || rule.location)}</td>
                    <td>{sourceLabel(rule, t)}</td>
                    <td><span className={statusClass(rule.status)}>{t(`sanitary:rules.statuses.${rule.status}`, { defaultValue: rule.status })}</span></td>
                    <td>
                      <span className={usage.used ? "sanitary-rule-usage sanitary-rule-usage--used" : "sanitary-rule-usage sanitary-rule-usage--unused"}>
                        {usage.used
                          ? t("sanitary:rules.overview.used", { count: usageCount })
                          : t("sanitary:rules.overview.unused")}
                      </span>
                    </td>
                    <td>
                      <div className="sanitary-rules-row-actions">
                        <button
                          type="button"
                          className="stock-md-button stock-md-button--small"
                          onClick={() => navigate(`/sanitary/rules/${rule.id}`)}
                          disabled={deletingRuleId === rule.id}
                        >
                          {canEdit ? t("sanitary:rules.actions.edit") : t("sanitary:rules.overview.view")}
                        </button>
                        {canEdit && usage.can_delete ? (
                          <button
                            type="button"
                            className="stock-md-button stock-md-button--small stock-md-button--dangerish"
                            onClick={() => handleDeleteRule(rule)}
                            disabled={Boolean(deletingRuleId)}
                          >
                            {deletingRuleId === rule.id
                              ? t("sanitary:rules.actions.deleting")
                              : t("sanitary:rules.actions.delete")}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <PaginationFooter
          className="sanitary-rules-pagination"
          page={Number(pageInfo.page || page)}
          pageCount={Number(pageInfo.page_count || 1)}
          pageSize={Number(pageInfo.page_size || pageSize)}
          pageSizeOptions={[25, 50, 100]}
          totalRows={Number(pageInfo.total || 0)}
          disabled={loading}
          onPageSizeChange={(value) => { setPageSize(value); setPage(1); }}
          onPrev={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => Math.min(Number(pageInfo.page_count || 1), current + 1))}
        />
      </section>
    </main>
  );
}
